import React from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { AppProvider } from "./contexts/AppContext";
import { Toaster } from "./components/ui/sonner";
import ProtectedRoute from "./components/ProtectedRoute";
import Layout from "./components/Layout";
import LoginPage from "./pages/LoginPage";
import AuthCallback from "./pages/AuthCallback";
import DashboardPage from "./pages/DashboardPage";
import EmployeesPage from "./pages/EmployeesPage";
import CompaniesPage from "./pages/CompaniesPage";
import SchedulesPage from "./pages/SchedulesPage";
import PayrollsPage from "./pages/PayrollsPage";
import LoansPage from "./pages/LoansPage";
import ContractsPage from "./pages/ContractsPage";
import GhostPage from "./pages/GhostPage";
import AdminPage from "./pages/AdminPage";
import UserFormPage from "./pages/UserFormPage";
import EmployeePortalPage from "./pages/EmployeePortalPage";
import CompliancePage from "./pages/CompliancePage";
import "./App.css";

function PermissionRoute({ permission, adminOnly, children }) {
  const { user, isAdmin } = useAuth();
  if (isAdmin) return children;
  if (adminOnly) return <Navigate to="/schedules" replace />;
  if (permission) {
    const perms = user?.permissions || {};
    if (perms[permission] === false) return <Navigate to="/schedules" replace />;
  }
  return children;
}

function AdminOnlyWithFallback({ fallback = "/schedules", children }) {
  const { isAdmin } = useAuth();
  if (!isAdmin) return <Navigate to={fallback} replace />;
  return children;
}

function DefaultRedirect() {
  const { isAdmin } = useAuth();
  return <Navigate to={isAdmin ? "/dashboard" : "/schedules"} replace />;
}

function AppRouter() {
  const location = useLocation();

  // Check for OAuth callback - process session_id BEFORE routing
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  // If accessing from dipendente/dipendenti.novahost.it or .app, redirect to /dipendente
  const hostname = window.location.hostname;
  if ((hostname === "dipendente.novahost.it" || hostname === "dipendenti.novahost.it" || 
       hostname === "dipendente.novahost.app" || hostname === "dipendenti.novahost.app") && 
      window.location.pathname !== "/dipendente") {
    return <Navigate to="/dipendente" replace />;
  }

  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/dipendente" element={<EmployeePortalPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <AdminOnlyWithFallback>
              <Layout>
                <DashboardPage />
              </Layout>
            </AdminOnlyWithFallback>
          </ProtectedRoute>
        }
      />
      <Route
        path="/employees"
        element={
          <ProtectedRoute>
            <Layout>
              <PermissionRoute permission="employees_view">
                <EmployeesPage />
              </PermissionRoute>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/companies"
        element={
          <ProtectedRoute>
            <Layout>
              <PermissionRoute permission="companies_view">
                <CompaniesPage />
              </PermissionRoute>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/schedules"
        element={
          <ProtectedRoute>
            <Layout>
              <PermissionRoute permission="schedules_view">
                <SchedulesPage />
              </PermissionRoute>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/payrolls"
        element={
          <ProtectedRoute>
            <Layout>
              <PermissionRoute permission="payrolls_view">
                <PayrollsPage />
              </PermissionRoute>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/loans"
        element={
          <ProtectedRoute>
            <Layout>
              <PermissionRoute adminOnly>
                <LoansPage />
              </PermissionRoute>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/contracts"
        element={
          <ProtectedRoute>
            <Layout>
              <PermissionRoute adminOnly>
                <ContractsPage />
              </PermissionRoute>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/ghost"
        element={
          <ProtectedRoute>
            <Layout>
              <PermissionRoute adminOnly>
                <GhostPage />
              </PermissionRoute>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <Layout>
              <AdminPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users/new"
        element={
          <ProtectedRoute>
            <Layout>
              <UserFormPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/users/:userId"
        element={
          <ProtectedRoute>
            <Layout>
              <UserFormPage />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/compliance"
        element={
          <ProtectedRoute>
            <Layout>
              <PermissionRoute adminOnly>
                <CompliancePage />
              </PermissionRoute>
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route path="/" element={<ProtectedRoute><DefaultRedirect /></ProtectedRoute>} />
      <Route path="*" element={<ProtectedRoute><DefaultRedirect /></ProtectedRoute>} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </AppProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
